import React, { useState, useEffect, useMemo } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { apigetPublic } from "../../service/api";

// ----------------------------
// Normalize branch data
// ----------------------------
function normalizeBranch(b) {
  if (!b) return null;

  return {
    id: b._id,
    name: b.name,
    city: b.city,
    state: b.stateCode,
    zip: String(b.zip || ""),   // <---- ALWAYS STRING
    address: b.address,
    branchSlug: b.name.toLowerCase().replace(/\s+/g, "-"),
  };
}

export default function InternalSearchAutocomplete({
  value,
  onChange,
  onSubmit,
}) {
  const [inputValue, setInputValue] = useState(value || "");

  const [branches, setBranches] = useState([]);
  const [states, setStates] = useState([]);
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [loading, setLoading] = useState(true);

  // ----------------------------
  // Pull full DB data
  // ----------------------------
  useEffect(() => {
    const loadData = async () => {
      try {
        const [branchRes, stateRes, loRes] = await Promise.all([
          apigetPublic("branches/search"),
          apigetPublic("states/search"),
          apigetPublic("loanofficer/search?location="),
        ]);

        if (branchRes?.status === 200) {
          setBranches(branchRes.data?.result || []);
        }
        if (stateRes?.status === 200) {
          setStates(stateRes.data?.result || []);
        }
        if (loRes?.status === 200 && Array.isArray(loRes.data?.result)) {
          setLoanOfficers(loRes.data?.result);
        }
      } catch (e) {
        console.error("Search data load error:", e);
      }
      setLoading(false);
    };

    loadData();
  }, []);

  // ----------------------------
  // Pre-normalized versions
  // ----------------------------
  const normalizedBranches = useMemo(
    () => branches.map((b) => normalizeBranch(b)).filter(Boolean),
    [branches]
  );

  // Flatten state → branch → ZIP for ZIP searching
  const stateZipIndex = useMemo(() => {
    const zipMap = {};
    states.forEach((s) => {
      if (s.zips) {
        s.zips.forEach((zip) => {
          zipMap[zip] = s;
        });
      }
    });
    return zipMap;
  }, [states]);

  // ----------------------------
  // BUILD AUTOCOMPLETE OPTIONS
  // ----------------------------
  const options = useMemo(() => {
    if (loading) return [];

    const v = inputValue.toLowerCase().trim();
    if (!v) return [];

    const zipRegex = /^\d{3,5}$/;
    const isZip = zipRegex.test(v);

    // ##############################
    // 1️⃣ STATE MATCHES
    // ##############################

    const stateOpts = states
      .filter((s) => {
        const name = s.name?.toLowerCase();
        const code = s.code?.toLowerCase();
        const zips = s.zips?.map((z) => String(z));

        return (
          name?.includes(v) ||
          code?.includes(v) ||
          (isZip && zips?.some((z) => z.startsWith(v)))
        );
      })
      .map((s) => ({
        type: "state",
        id: s._id,
        label: `${s.name}`,
        slug: s.code?.toLowerCase(),
      }));

    // ##############################
    // 2️⃣ LOAN OFFICER MATCHES
    // ##############################

    const officerOpts = loanOfficers
      .filter((lo) => {
        const fields = [
          lo.firstName,
          lo.lastName,
          lo.slug,
          `${lo.firstName} ${lo.lastName}`,
          lo.city,
          lo.state,
          lo.zip,
        ]
          .filter(Boolean)
          .map((x) => x.toLowerCase());

        if (isZip) return fields.some((f) => f.startsWith(v));
        return fields.some((f) => f.includes(v));
      })
      .map((lo) => ({
        type: "officer",
        id: lo.slug,
        label: `${lo.firstName} ${lo.lastName}`,
        slug: lo.slug,
      }));

    // ##############################
    // 3️⃣ BRANCH MATCHES
    // ##############################

    const branchOpts = normalizedBranches
      .filter((b) => {
        const fields = [
          b.name,
          b.city,
          b.state,
           String(b.zip),
          b.address,
        ]
          .filter(Boolean)
          .map((x) => x.toLowerCase());

        if (isZip) return fields.some((f) => f.startsWith(v));
        return fields.some((f) => f.includes(v));
      })
      .map((b) => ({
        type: "branch",
        id: b.id,
        label: `${b.name} – ${b.city}, ${b.state}`,
        slug: b.branchSlug,
      }));

    return [...stateOpts, ...officerOpts, ...branchOpts];
  }, [
    inputValue,
    loading,
    states,
    loanOfficers,
    normalizedBranches,
  ]);

  // ----------------------------
  // AUTOCOMPLETE RENDER
  // ----------------------------
  return (
    <Autocomplete
      loading={loading}
      freeSolo
      options={options}
      groupBy={(opt) =>
        opt.type === "state"
          ? "States"
          : opt.type === "officer"
          ? "Loan Officers"
          : "Branches"
      }
      getOptionLabel={(opt) =>
        typeof opt === "string" ? opt : opt.label
      }
      inputValue={inputValue}
      onInputChange={(_, newValue) => {
  setInputValue(newValue);
  onChange?.(newValue); // <-- REQUIRED so parent can filter
}}

      onChange={(_, newValue) => {
        if (!newValue) return;

        if (typeof newValue === "string") {
          onSubmit?.(newValue);
          return;
        }

        onChange?.(newValue.label);

        if (newValue.type === "officer") {
          window.location.href = `/${newValue.slug}`;
        } else if (newValue.type === "branch") {
          window.location.href = `/branch/${newValue.slug}`;
        } else if (newValue.type === "state") {
          onSubmit?.({ type: "state", stateCode: newValue.slug });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search by  branch, state or LO"
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit?.(inputValue);
            }
          }}
        />
      )}
    />
  );
}
